import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Field,
  FieldArray
} from 'redux-form';

import { BOOKING_REQUEST_FORM } from './forms/constants';

import DateTime from './DateTime';
import MobileDateTime from './MobileDateTime';
import { ActivityDropdown } from 'shared-components/search-module';
import { ActivityInputTray } from 'shared-components/trays/activity/activity-input-tray';
import withActivityDropdownHoc from '../app-listing/activity-dropdown-hoc';
import withActivityMobileTrayHoc from '../app-listing/mobile-activity-tray-hoc';
import ToggleField from '../app-main/components/reduxForms/fields/ToggleField/ToggleField';
import NumberField from '../app-main/components/reduxForms/fields/NumberField/NumberField';
import { LightbulbTip } from '../elements-tooltip/LightbulbTip';
import { get, isEmpty, isNull } from 'lodash';
import { FormattedMessage, injectIntl } from 'react-intl';
import { priceOptionLabel } from '../app-bookings-custom/utils';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';


const ActivityInput = withActivityDropdownHoc(ActivityDropdown);
const MobileActivityInput = withActivityMobileTrayHoc(ActivityInputTray);

class RequestBookingDetails extends Component {
  componentDidMount () {
    this.props.loadBookingsTrack('viewed bkg request form');
    window.scrollTo(0, 0);
  }

  render () {
    const {
      scheduleInput = [{}],
      spaceId,
      getFeeCalc,
      dispatch = null,
      reachedMaxDays,
      getActivityValue = null,
      activityValue = {},
      handleAttendeesChange = null,
      showAttendeeIBWarning = false,
      numberGuests,
      schedule = [{}],
      errorMessage = '',
      useType,
      isInstaBookingWithValidAttendees = false,
      listing,
      intl,
      billingCountryValue,
      handleBillingCountryChange = null,
      handleBillingSubRegionChange = null,
      params
    } = this.props;

    //no need to collect billing info if they have VAT address
    //jurisdiction is apply VAT address, then billing and lastly country of preferred locale
    const zendeskLocale = SharedIntlProviderHOC.getZendeskLocale();
    const selectedActivity = get(activityValue, 'tags.activity');

    return (
      <div className="RequestBookingDetails">
        <div className="margin-lg-bottom">
          <div className="heavier">
            <FormattedMessage
              id="booking_request.booking_details.date_time"
              defaultMessage="Date & Time"
            />
          </div>

          <FieldArray
            name="scheduleInput"
            component={DateTime}
            schedule={schedule}
            scheduleInput={scheduleInput}
            optional={false}
            spaceId={spaceId}
            getFeeCalc={getFeeCalc}
            formName={BOOKING_REQUEST_FORM}
            dispatch={dispatch}
            reachedMaxDays={reachedMaxDays}
            isBookingForm
            multiday
            isLabel
            country={ get(listing, 'country', '') }
          />

          {
            reachedMaxDays
              && <div className="help-block">
              <FormattedMessage
                id='booking_request.booking_details.max_days'
                defaultMessage='Please contact our support team {link} to complete this booking'
                values={{
                  link: (
                    <a target="_blank" className="underline" href={`https://support.peerspace.com/hc/${zendeskLocale}/requests/new`}>
                      <FormattedMessage
                        id="booking_request.booking_details.here"
                        defaultMessage="here"
                      />
                    </a>
                  )
                }}
              />
            </div>
          }

          <div className="help-block text-red">{errorMessage}</div>
        </div>

        <div className="activity-input-section margin-xl-bottom">
          <div className="heavier margin-md-bottom">
            <FormattedMessage
              id="booking_request.booking_details.what_are_you_planning"
              defaultMessage="What are you planning?"
            />
          </div>
          <Field
            name="tags"
            component={ActivityInput}
            bookingActivity={selectedActivity}
            listingUseType={useType}
            getValueFromInput={getActivityValue}
            excludeCustomActivities
          />
        </div>

        <div className="margin-xl-bottom">
          <div className="heavier margin-md-bottom">
            <FormattedMessage
              id="booking_request.booking_details.attendees"
              defaultMessage="How many people will attend?"
            />
          </div>
          <div className="number-of-guests-wrap">
            <Field
              name="number_of_guests"
              component={NumberField}
              attributes={{ 'data-testing-id': 'number-guests-field', id: 'number-of-guests' }}
              parse={value => Number(value) || null}
              placeholder={ intl.formatMessage({ id: 'booking_request.booking_details.attendee_placeholder', defaultMessage: 'Max capacity is {numberGuests}'}, { numberGuests } )}
              onBlur={handleAttendeesChange}
            />
          </div>
        </div>

        { showAttendeeIBWarning
          && <LightbulbTip
            hasBox
            description={
              <FormattedMessage
                id="booking_request.booking_details.attendee_max"
                defaultMessage="This listing allows up to {numberGuests} attendees. The number of attendees you entered requires host approval. Reduce your number of attendees to Instant Book."
                values={{
                  numberGuests
                }}
              />
            }
            className="flex margin-xl-bottom"
          />
        }

        <div className="flex column alcohol-toggle-section">
          <div className="heavier margin-md-bottom">
            <FormattedMessage
              id="booking_request.booking_details.alcohol_toggle"
              defaultMessage="Will alcohol be consumed?"
            />
          </div>
          <Field
            name="alcohol_consumed"
            component={ToggleField}
            attributes={{
              'data-testing-id': 'alcohol-consumed-toggle',
              name: 'alcohol_consumed'
            }}
            firstOption={
              {
                id: 'alcohol-consumed-true',
                value: 'true',
                label: intl.formatMessage({ id: 'booking_request.booking_details.yes', defaultMessage: 'Yes' })
              }}
            secondOption={
              {
                id: 'alcohol-consumed-false',
                value: 'false',
                label: intl.formatMessage({ id: 'booking_request.booking_details.no', defaultMessage: 'No' })
              }
            }
          />
        </div>
      </div>
    );
  }
}

RequestBookingDetails.propTypes = {
  scheduleInput: PropTypes.object,
  spaceId: PropTypes.string,
  getFeeCalc: PropTypes.func,
  dispatch: PropTypes.func,
  reachedMaxDays: PropTypes.bool,
  listingUseType: PropTypes.string,
  getActivityValue: PropTypes.func,
  handleAttendeesChange: PropTypes.func,
  showAttendeeIBWarning: PropTypes.bool,
  numberGuests: PropTypes.number,
  schedule: PropTypes.array,
  errorMessage: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string
  ]),
  useType: PropTypes.string,
  isInstaBookingWithValidAttendees: PropTypes.bool,
  loadBookingsTrack: PropTypes.func,
  booking: PropTypes.object,
  listing: PropTypes.object
};

export default injectIntl(RequestBookingDetails)
